import React from 'react';
import { GUIDE_CONFIG } from '../../config';
import { formatAssetUrl } from '../../services/apiService';

/** Trích xuất video ID từ URL YouTube (youtube.com/watch?v=ID hoặc youtu.be/ID) */
export function getYouTubeVideoId(urlOrId: string): string | null {
    if (!urlOrId || typeof urlOrId !== 'string') return null;
    const s = urlOrId.trim();
    const ytMatch = s.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
    if (ytMatch) return ytMatch[1];
    if (/^[a-zA-Z0-9_-]{10,12}$/.test(s)) return s;
    return null;
}

/** Trích xuất video ID từ URL Vimeo */
export function getVimeoVideoId(url: string): string | null {
    if (!url || typeof url !== 'string') return null;
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
}

/** Có thể hiển thị khối video/HTML từ GUIDE_CONFIG hay không */
export function hasGuideVideoOrContent(): boolean {
    const videoSource = GUIDE_CONFIG.VIDEO_SOURCE || 'youtube';
    const youtubeId = getYouTubeVideoId(GUIDE_CONFIG.YOUTUBE_VIDEO_ID || '') || GUIDE_CONFIG.YOUTUBE_VIDEO_ID || null;
    const videoUrl = GUIDE_CONFIG.VIDEO_URL;
    const hasVideo = (videoSource === 'youtube' && youtubeId) || (videoSource === 'server' && videoUrl);
    const content = (GUIDE_CONFIG.CONTENT || '').trim();
    return !!(hasVideo || content);
}

interface GuideVideoSectionProps {
    className?: string;
    title?: string;
}

/**
 * Video + nội dung HTML từ GUIDE_CONFIG (dùng trong modal onboarding hoặc màn hướng dẫn theo vai trò).
 */
const GuideVideoSection: React.FC<GuideVideoSectionProps> = ({ className = '', title: titleProp }) => {
    const videoSource = GUIDE_CONFIG.VIDEO_SOURCE || 'youtube';
    const youtubeId = getYouTubeVideoId(GUIDE_CONFIG.YOUTUBE_VIDEO_ID || '') || GUIDE_CONFIG.YOUTUBE_VIDEO_ID || null;
    const videoUrl = GUIDE_CONFIG.VIDEO_URL;
    const title = titleProp ?? (GUIDE_CONFIG.TITLE || 'Hướng dẫn sử dụng');
    const content = GUIDE_CONFIG.CONTENT || '';

    const hasVideo = (videoSource === 'youtube' && youtubeId) || (videoSource === 'server' && videoUrl);
    const videoSrc = videoSource === 'server' && videoUrl ? formatAssetUrl(videoUrl) || videoUrl : null;

    const renderVideo = () => {
        if (!hasVideo) return null;
        if (videoSource === 'youtube' && youtubeId) {
            return (
                <iframe
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    title={title}
                    className="w-full aspect-video rounded-xl"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                />
            );
        }
        const vimeoId = getVimeoVideoId(videoUrl || '');
        if (vimeoId) {
            return (
                <iframe
                    src={`https://player.vimeo.com/video/${vimeoId}`}
                    title={title}
                    className="w-full aspect-video rounded-xl"
                    allowFullScreen
                />
            );
        }
        if (videoSrc) {
            return (
                <video src={videoSrc} controls className="w-full aspect-video rounded-xl" playsInline />
            );
        }
        return null;
    };

    if (!hasVideo && !content) return null;

    return (
        <div className={className}>
            {hasVideo && (
                <div className="mb-4 rounded-xl overflow-hidden bg-slate-900 shadow-inner">{renderVideo()}</div>
            )}
            {content && (
                <div
                    className="text-sm text-slate-600 leading-relaxed mb-4 prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0 text-left"
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            )}
        </div>
    );
};

export default GuideVideoSection;
